import { createContext, useCallback, useContext, useEffect, useRef, useState } from 'react';
import type { ReactNode } from 'react';
import { Box } from '../primitives';
import { visuallyHidden } from '../theme/focus';

export type Politeness = 'polite' | 'assertive';

export interface AnnouncerApi {
  /**
   * Announce a message to assistive technology.
   *
   * `polite` (default) waits for the user to be idle — right for save
   * confirmations, "document ready", and result counts. `assertive` interrupts
   * — reserve it for validation errors and failures.
   */
  announce: (message: string, politeness?: Politeness) => void;
}

const AnnouncerContext = createContext<AnnouncerApi | null>(null);

/**
 * A single pair of app-wide ARIA live regions.
 *
 * Async outcomes in these apps — a generated document becoming ready, a stage
 * saving, a filter narrowing a list — changed the screen silently. WCAG 4.1.3
 * (Status Messages) needs them announced without moving focus. A shared pair of
 * regions mounted once at the root is the reliable way to do that: regions
 * created at the moment of the update are frequently missed by screen readers,
 * which is exactly the bug already noted in the bulletin page's own comments.
 */
export function AnnouncerProvider({ children }: { children: ReactNode }) {
  const [polite, setPolite] = useState('');
  const [assertive, setAssertive] = useState('');
  const timers = useRef<ReturnType<typeof setTimeout>[]>([]);

  useEffect(() => {
    const pending = timers.current;
    return () => pending.forEach(clearTimeout);
  }, []);

  const announce = useCallback((message: string, politeness: Politeness = 'polite') => {
    const set = politeness === 'assertive' ? setAssertive : setPolite;
    // Clear first so repeating the same message still reads as a change.
    set('');
    timers.current.push(setTimeout(() => set(message), 60));
  }, []);

  return (
    <AnnouncerContext.Provider value={{ announce }}>
      {children}
      <Box aria-live="polite" aria-atomic="true" role="status" sx={visuallyHidden}>
        {polite}
      </Box>
      <Box aria-live="assertive" aria-atomic="true" role="alert" sx={visuallyHidden}>
        {assertive}
      </Box>
    </AnnouncerContext.Provider>
  );
}

/**
 * Announce async results to assistive technology. Safe to call outside an
 * `AnnouncerProvider` — it degrades to a no-op rather than throwing, so a
 * component can use it without forcing every test to wrap in the provider.
 */
export function useAnnouncer(): AnnouncerApi {
  return useContext(AnnouncerContext) ?? NOOP_ANNOUNCER;
}

const NOOP_ANNOUNCER: AnnouncerApi = { announce: () => undefined };
